import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  inject,
  Input,
  Renderer2,
  signal,
  ViewChild,
} from '@angular/core';
import { timer } from 'rxjs';

/**
 * Компонент `SdvorTextareaComponent` используется для ввода данных от пользователя
 *
 * ```ts
 * import { SdvorTextareaComponent } from '@sdvor/shared/ui/v2';
 * ```
 */
@Component({
  selector: 'sd-textarea',
  templateUrl: './textarea.component.html',
  styleUrls: ['./textarea.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
})
export class SdvorTextareaComponent implements AfterViewInit {
  /** Текстовая подсказка */
  @Input() label?: string;
  /** Комментарий, отображаемый под полем ввода */
  @Input() comment?: string;
  /** Ошибка, если содержит текст, то поле считается не валидным */
  @Input() error?: string | null;

  hasValue = signal(false);

  private isInitialized = false;
  private isFocused: boolean;
  private placeholder: string | undefined;
  private render = inject(Renderer2);

  @HostBinding('class.form-field-error')
  // @ts-expect-error no-unused-variable
  private get hasError(): boolean {
    return Boolean(this.error);
  }

  @ViewChild('labelWrapper', { read: ElementRef })
  private labelWrapper: ElementRef<HTMLTextAreaElement>;

  ngAfterViewInit(): void {
    // значение инпута не успевает проставиться, поэтому используем задержку
    timer(0).subscribe(() => {
      const inputEl: HTMLTextAreaElement | null = this.labelWrapper.nativeElement.querySelector('textarea');

      if (inputEl) {
        this.updateValue(inputEl.value);
        this.updatePlaceholderValue(inputEl.attributes.getNamedItem('placeholder')?.value);
        this.initialPlaceholderVisibility(inputEl);
        this.isInitialized = true;
      }
    });
  }

  @HostListener('focusin', ['$event'])
  // @ts-expect-error no-unused-variable
  private onFocusin(e: Event<HTMLTextAreaElement>): void {
    this.isFocused = true;

    // если инпут не инициализирован, то просто проставляем
    // флаг что он в фокусе, но с плейсхолдером ничего не делаем
    if (!this.isInitialized) {
      return;
    }

    // при фокусе возвращаем плейсхолдер, так как лейбл уходит на верх над инпутом
    this.showPlaceholder(e.target);
  }

  @HostListener('focusout', ['$event'])
  // @ts-expect-error no-unused-variable
  private onFocusout(e: Event<HTMLTextAreaElement>): void {
    this.isFocused = false;

    // если инпут не инициализирован, то просто проставляем
    // флаг что он не в фокусе, но с плейсхолдером ничего не делаем
    if (!this.isInitialized) {
      return;
    }

    this.updateValue(e.target.value);

    this.hidePlaceholder(e.target);
  }

  @HostListener('input', ['$event'])
  // @ts-expect-error no-unused-variable
  private onInput(e: Event<HTMLInputElement>): void {
    this.updateValue(e.target.value);
    this.updateElHeight(e.target);
  }

  private updateElHeight(el: HTMLTextAreaElement): void {
    el.style.height = '1px';
    el.style.height = el.scrollHeight + 1 + 'px';
  }

  private updateValue(value: string | undefined): void {
    if (Boolean(value) !== this.hasValue()) {
      this.hasValue.set(Boolean(value));
    }
  }

  private updatePlaceholderValue(value: string | undefined): void {
    if (this.placeholder !== value) {
      this.placeholder = value;
    }
  }

  private initialPlaceholderVisibility(inputEl: HTMLTextAreaElement): void {
    // если инпут находится в фокусе, то плейсхолдер не прячем
    if (this.isFocused) {
      return;
    }

    // прячем плейсхолдер, чтобы он не накладывался на лейбл
    if (this.placeholder) {
      this.hidePlaceholder(inputEl);
    }
  }

  private hidePlaceholder(input: HTMLTextAreaElement): void {
    if (!this.label) {
      return;
    }

    this.render.removeAttribute(input, 'placeholder');
  }

  private showPlaceholder(input: HTMLTextAreaElement): void {
    if (!this.label) {
      return;
    }

    this.render.setAttribute(input, 'placeholder', this.placeholder || '');
  }
}
